import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import axios from 'axios';
import Login from '@/pages/Login';
import Dashboard from '@/pages/Dashboard';
import Welcome from '@/pages/Welcome';
import Employees from '@/pages/Employees';
import Reports from '@/pages/Reports';
import Camera from '@/pages/Camera';
import Settings from '@/pages/Settings';
import ProfileSettings from '@/pages/settings/ProfileSettings';
import BackendSettings from '@/pages/settings/BackendSettings';
import UserManagement from '@/pages/settings/UserManagement';
import PermissionsAudit from '@/pages/settings/PermissionsAudit';
import DataIntegrity from '@/pages/settings/DataIntegrity';
import CompanyProfile from '@/pages/settings/CompanyProfile';

// Production Department Pages
import ProductionReport from '@/pages/production/ProductionReport';
import DutyRoster from '@/pages/production/DutyRoster';
import MaterialRecovery from '@/pages/production/MaterialRecovery';
import CrucibleReport from '@/pages/production/CrucibleReport';
import ProductionPlan from '@/pages/production/ProductionPlan';
import BatchPlan from '@/pages/production/BatchPlan';
import FurnaceDashboard from '@/pages/production/FurnaceDashboard';
import ProductionDataEntry from '@/pages/production/ProductionDataEntry';

// Raw Material Department Pages
import RawMaterialStock from '@/pages/raw-material/RawMaterialStock';
import RawMaterialDutyRoster from '@/pages/raw-material/RawMaterialDutyRoster';
import MachinePlanning from '@/pages/raw-material/MachinePlanning';
import DrossStock from '@/pages/raw-material/DrossStock';
import FerrousScrap from '@/pages/raw-material/FerrousScrap';
import RedsHeavies from '@/pages/raw-material/RedsHeavies';
import HeavyMetals from '@/pages/raw-material/HeavyMetals';
import RawMaterialWeighment from '@/pages/raw-material/RawMaterialWeighment';
import ProcessingMachines from '@/pages/raw-material/ProcessingMachines';
import DailyProcessingPlan from '@/pages/raw-material/DailyProcessingPlan';
import RawMaterialGRN from '@/pages/raw-material/GRN';
import SupplierDashboard from '@/pages/raw-material/SupplierDashboard';
import RawMaterialPurchaseOrder from '@/pages/raw-material/PurchaseOrder';
import RawMaterialConsumptionStock from '@/pages/raw-material/ConsumptionStock';

// Dispatch Department Pages
import OrderDetails from '@/pages/dispatch/OrderDetails';
import DeliveryDetails from '@/pages/dispatch/DeliveryDetails';
import VehicleTracking from '@/pages/dispatch/VehicleTracking';
import WeighmentDetails from '@/pages/dispatch/WeighmentDetails';
import PackingConsumables from '@/pages/dispatch/PackingConsumables';
import DispatchDutyRoster from '@/pages/dispatch/DispatchDutyRoster';
import DispatchRecords from '@/pages/dispatch/DispatchRecords';
import FinishedGoodsStock from '@/pages/dispatch/FinishedGoodsStock';
import CustomerOrders from '@/pages/dispatch/CustomerOrders';

// Stores Department Pages
import ConsumableStock from '@/pages/stores/ConsumableStock';
import GRN from '@/pages/stores/GRN';
import DeletedGRNs from '@/pages/stores/DeletedGRNs';
import StockHealth from '@/pages/stores/StockHealth';
import PaymentsHealth from '@/pages/stores/PaymentsHealth';
import QA from '@/pages/stores/QA';
import StoresWeighment from '@/pages/stores/Weighment';
import Rejections from '@/pages/stores/Rejections';
import ConsumablePlanning from '@/pages/stores/ConsumablePlanning';
import MaterialRequisitions from '@/pages/stores/MaterialRequisitions';
import PurchaseOrder from '@/pages/stores/PurchaseOrder';
import StoresPurchaseOrderV2 from '@/pages/stores/PurchaseOrderV2';
import Tenders from '@/pages/stores/Tenders';
import POvsDelivered from '@/pages/stores/POvsDelivered';
import StoresPayments from '@/pages/stores/Payments';

// Imports Department Pages
import ImportsPage from '@/pages/imports/ImportsPage';
import SalesContractDirectory from '@/pages/imports/SalesContractDirectory';
import ImportPOvsDelivered from '@/pages/imports/ImportPOvsDelivered';
import ImportDocuments from '@/pages/imports/ImportDocuments';
import ShipmentTracking from '@/pages/imports/ShipmentTracking';
import ImportClaims from '@/pages/imports/Claims';
import ClaimsDashboard from '@/pages/imports/ClaimsDashboard';

// Projects Department Pages
import OngoingProjects from '@/pages/projects/OngoingProjects';
import CompletedProjects from '@/pages/projects/CompletedProjects';
import UpcomingProjects from '@/pages/projects/UpcomingProjects';
import ProjectPurchaseOrders from '@/pages/projects/ProjectPurchaseOrders';
import ProjectGRN from '@/pages/projects/ProjectGRN';
import ProjectQA from '@/pages/projects/ProjectQA';
import ProjectConsumption from '@/pages/projects/ProjectConsumption';
import ProjectPayments from '@/pages/projects/ProjectPayments';

// Maintenance Department Pages
import PlantMachinery from '@/pages/maintenance/PlantMachinery';
import MaintenanceLog from '@/pages/maintenance/MaintenanceLog';
import ReportShutdown from '@/pages/maintenance/ReportShutdown';
import MaintenanceDutyRoster from '@/pages/maintenance/MaintenanceDutyRoster';
import MachineDrawings from '@/pages/maintenance/MachineDrawings';

// HR Department Pages
import MasterRoll from '@/pages/hr/MasterRoll';
import SalaryDetails from '@/pages/hr/SalaryDetails';
import Leave from '@/pages/hr/Leave';
import HRCalendar from '@/pages/hr/Calendar';
import Attendance from '@/pages/hr/Attendance';
import Payroll from '@/pages/hr/Payroll';
import OrgStructure from '@/pages/hr/OrgStructure';
import Grievances from '@/pages/hr/Grievances';

// Master Data
import MasterData from '@/pages/master-data/MasterData';

// Placeholder pages for other departments
import PlaceholderPage from '@/pages/PlaceholderPage';

import Layout from '@/components/Layout';
import ProtectedRoute from '@/components/ProtectedRoute';
import HomeRoute from '@/components/HomeRoute';
import { Toaster } from 'sonner';
import '@/App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios to include token in all requests
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Removed withCredentials - not needed for JWT auth and can cause CORS issues on mobile
  return config;
});

// Response interceptor — ONLY clear the auth token on a real 401 (Unauthorized).
// Network errors, 5xx server errors, Cloudflare 524 timeouts, etc. must NEVER
// kick the user out of the session. Previously, ANY error wiped the token,
// which caused the "data disappears + forced re-login loop" reported by users
// on high-data pages.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      const onLogin = (typeof window !== 'undefined') && window.location?.pathname?.includes('/login');
      if (!onLogin) {
        localStorage.removeItem('authToken');
        // Soft-redirect — let the SPA's router handle the login mount.
        if (typeof window !== 'undefined') window.location.assign('/login');
      }
    }
    return Promise.reject(error);
  }
);

export const AppContext = React.createContext();

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('erp-theme');
    if (savedTheme === 'dark') {
      return 'light';
    }
    return 'light';
  });

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    const isDark = theme === 'dark';
    document.documentElement.classList.toggle('dark', isDark);
    document.documentElement.style.colorScheme = isDark ? 'dark' : 'light';
    if (theme === 'dark') {
      localStorage.setItem('erp-theme', 'dark');
    } else {
      localStorage.setItem('erp-theme', 'light');
    }
  }, [theme]);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setUser(null);
        setLoading(false);
        return;
      }
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      // CRITICAL: only treat a real 401 as "not authenticated".
      // Network blips, 5xx errors, Cloudflare 524 timeouts, etc. must NOT
      // wipe the token — the user's session is still valid; we just couldn't
      // reach the server momentarily. Previously this block ran on EVERY
      // error, which caused the "high-data page + refresh → forced re-login
      // loop" production bug.
      if (error?.response?.status === 401) {
        localStorage.removeItem('authToken');
        setUser(null);
      } else {
        // Keep the token; render an unauthenticated state for this mount but
        // do NOT delete it — next refresh / reconnect will rehydrate.
        // We still set user to null so the app shows login if /me failed for
        // a network reason — but the token survives so the user only needs
        // to click "Login" without re-entering credentials? Actually safer:
        // optimistically rehydrate from any cached user info if available.
        const cached = localStorage.getItem('cachedUser');
        if (cached) {
          try { setUser(JSON.parse(cached)); } catch { setUser(null); }
        } else {
          setUser(null);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const data = response.data;

    // Store JWT token in localStorage
    if (data.token) {
      localStorage.setItem('authToken', data.token);
    }
    // Cache the user shape so a transient /auth/me failure doesn't bounce
    // the user back to the login screen.
    try { localStorage.setItem('cachedUser', JSON.stringify(data)); } catch { /* quota — ignore */ }

    setUser(data);
    return data;
  };

  const toggleTheme = () => {
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch (e) {
      // Ignore logout errors
    }
    localStorage.removeItem('authToken');
    localStorage.removeItem('cachedUser');
    setUser(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="flex flex-col items-center space-y-4">
          <img src="/logo.jpg" alt="Swift Strips India" className="h-16 w-16 object-contain animate-pulse" />
          <div className="text-lg font-medium text-gray-600">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <AppContext.Provider value={{ user, login, logout, API, theme, toggleTheme }}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={user ? <Navigate to="/" /> : <Login />} />
          <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            {/* Home - Shows Dashboard or Welcome based on permissions */}
            <Route index element={<HomeRoute />} />
            
            {/* Production Department */}
            <Route path="production/report" element={<ProductionReport />} />
            <Route path="production/duty-roster" element={<DutyRoster />} />
            <Route path="production/material-recovery" element={<MaterialRecovery />} />
            <Route path="production/crucible-report" element={<CrucibleReport />} />
            <Route path="production/production-plan" element={<ProductionPlan />} />
            <Route path="production/batch-plan" element={<BatchPlan />} />
            <Route path="production/furnace-dashboard" element={<FurnaceDashboard />} />
            <Route path="production/data-entry" element={<ProductionDataEntry />} />
            
            {/* Raw Material Department */}
            <Route path="raw-material/stock" element={<RawMaterialStock />} />
            <Route path="raw-material/duty-roster" element={<RawMaterialDutyRoster />} />
            
            {/* Redirects for old raw-material URLs moved to Imports */}
            <Route path="raw-material/grn" element={<Navigate to="/imports/grn" replace />} />
            <Route path="raw-material/purchase-order" element={<Navigate to="/imports/purchase-order" replace />} />
            <Route path="raw-material/supplier-dashboard" element={<Navigate to="/imports/supplier-dashboard" replace />} />
            
            {/* Master Data - Raw Material Consumption & Stock */}
            <Route path="master-data/raw-material/consumption-stock" element={<RawMaterialConsumptionStock />} />
            <Route path="raw-material/machine-planning" element={<MachinePlanning />} />
            <Route path="raw-material/dross-stock" element={<DrossStock />} />
            <Route path="raw-material/ferrous-scrap" element={<FerrousScrap />} />
            <Route path="raw-material/reds-heavies" element={<RedsHeavies />} />
            <Route path="raw-material/heavy-metals" element={<HeavyMetals />} />
            <Route path="raw-material/weighment" element={<RawMaterialWeighment />} />
            <Route path="raw-material/processing-machines" element={<ProcessingMachines />} />
            <Route path="raw-material/processing-plan" element={<DailyProcessingPlan />} />
            
            {/* Dispatch Department */}
            <Route path="dispatch/orders" element={<OrderDetails />} />
            <Route path="dispatch/delivery" element={<DeliveryDetails />} />
            <Route path="dispatch/vehicle-tracking" element={<VehicleTracking />} />
            <Route path="dispatch/weighment" element={<WeighmentDetails />} />
            <Route path="dispatch/packing" element={<PackingConsumables />} />
            <Route path="dispatch/duty-roster" element={<DispatchDutyRoster />} />
            <Route path="dispatch/records" element={<DispatchRecords />} />
            <Route path="dispatch/finished-goods" element={<FinishedGoodsStock />} />
            <Route path="dispatch/customer-orders" element={<CustomerOrders />} />
            
            {/* Stores Department */}
            <Route path="stores/consumable-stock" element={<ConsumableStock />} />
            <Route path="stores/service-consumption" element={<ConsumableStock poType="service" />} />
            <Route path="stores/grn" element={<GRN />} />
            <Route path="stores/grn/deleted" element={<DeletedGRNs />} />
            <Route path="stores/consumable-stock/health" element={<StockHealth />} />
            <Route path="stores/service-consumption/health" element={<StockHealth poType="service" />} />
            <Route path="stores/payments/health" element={<PaymentsHealth />} />
            <Route path="stores/qa" element={<QA />} />
            <Route path="stores/weighment" element={<StoresWeighment />} />
            <Route path="stores/rejections" element={<Rejections />} />
            <Route path="stores/consumable-planning" element={<ConsumablePlanning />} />
            <Route path="stores/material-requisitions" element={<MaterialRequisitions />} />
            <Route path="stores/purchase-order" element={<StoresPurchaseOrderV2 />} />
            <Route path="stores/purchase-order-old" element={<PurchaseOrder />} />
            <Route path="stores/tenders" element={<Tenders />} />
            <Route path="stores/po-vs-delivered" element={<POvsDelivered />} />
            <Route path="stores/payments" element={<StoresPayments />} />
            
            {/* Imports Department */}
            <Route path="imports" element={<ImportsPage />} />
            <Route path="imports/supplier-dashboard" element={<SupplierDashboard />} />
            <Route path="imports/purchase-order" element={<RawMaterialPurchaseOrder />} />
            <Route path="imports/grn" element={<RawMaterialGRN />} />
            <Route path="imports/sales-contract" element={<SalesContractDirectory />} />
            <Route path="imports/po-vs-delivered" element={<ImportPOvsDelivered />} />
            <Route path="imports/shipments" element={<ShipmentTracking />} />
            <Route path="imports/documents" element={<ImportDocuments />} />
            <Route path="imports/claims" element={<ImportClaims />} />
            <Route path="imports/claims-dashboard" element={<ClaimsDashboard />} />
            
            {/* Projects Department */}
            <Route path="projects/ongoing" element={<OngoingProjects />} />
            <Route path="projects/completed" element={<CompletedProjects />} />
            <Route path="projects/upcoming" element={<UpcomingProjects />} />
            <Route path="projects/purchase-orders" element={<ProjectPurchaseOrders />} />
            <Route path="projects/grn" element={<ProjectGRN />} />
            <Route path="projects/qa" element={<ProjectQA />} />
            <Route path="projects/material-consumption" element={<ProjectConsumption poType="material" />} />
            <Route path="projects/service-consumption" element={<ProjectConsumption poType="service" />} />
            <Route path="projects/payments" element={<ProjectPayments /> } />
            
            {/* Maintenance Department */}
            <Route path="maintenance/machinery" element={<PlantMachinery />} />
            <Route path="maintenance/log" element={<MaintenanceLog />} />
            <Route path="maintenance/shutdown" element={<ReportShutdown />} />
            <Route path="maintenance/duty-roster" element={<MaintenanceDutyRoster />} />
            <Route path="maintenance/drawings" element={<MachineDrawings />} />
            
            {/* HR Department */}
            <Route path="hr/master-roll" element={<MasterRoll />} />
            <Route path="hr/salary" element={<SalaryDetails />} />
            <Route path="hr/leave" element={<Leave />} />
            <Route path="hr/calendar" element={<HRCalendar />} />
            <Route path="hr/attendance" element={<Attendance />} />
            <Route path="hr/payroll" element={<Payroll />} />
            <Route path="hr/org-structure" element={<OrgStructure />} />
            <Route path="hr/grievances" element={<Grievances />} />
            
            {/* Master Data */}
            <Route path="master-data/:department" element={<MasterData />} />
            
            {/* Other Pages */}
            <Route path="reports" element={<Reports />} />
            <Route path="camera" element={<Camera />} />
            
            {/* Settings with nested routes */}
            <Route path="settings" element={<Settings />}>
              <Route index element={<ProfileSettings />} />
              <Route path="company" element={<CompanyProfile />} />
              <Route path="backend" element={<BackendSettings />} />
              <Route path="users" element={<UserManagement />} />
              <Route path="permissions-audit" element={<PermissionsAudit />} />
              <Route path="data-integrity" element={<DataIntegrity />} />
            </Route>
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster position="bottom-right" richColors />
    </AppContext.Provider>
  );
}

export default App;
